import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Save, Trash2, AlertTriangle, Loader2, Plus, Minus, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { compressProductImage } from '../utils/imageCompressor';

interface Product {
  id: number;
  name: string;
  category: string;
  categoryId?: number | null;
  price: number;
  costPrice?: number;
  sellingPrice?: number;
  stockCount: number;
  unitType?: 'Pieces' | 'Kg' | 'Liters' | 'Grams' | 'ml';
  emoji?: string;
  color?: string;
  coreCharge?: number;
  barcode?: string | null;
  productImage?: string;
  warrantyPeriod?: string | null;
  returnPolicy?: string | null;
  baseModel?: string | null;
  lunaVisibility?: string;
  minPrice?: number | null;
  maxPrice?: number | null;
  priceList?: Array<{ label: string; price: number }> | null;
  expiryDate?: string | null;
}

interface Category {
  id: number;
  name: string;
  warrantyPeriod?: string | null;
  returnPolicy?: string | null;
}

interface EditProductModalProps {
  product: Product;
  storeApiKey: string;
  onClose: () => void;
  onSaved: () => void;
}

const UNIT_OPTIONS = ['Pieces', 'Kg', 'Grams', 'Liters', 'ml'];
const WARRANTY_OPTIONS = ['No Warranty', 'Standard Warranty', 'Extended Warranty (1 Year)'];

export default function EditProductModal({ product, storeApiKey, onClose, onSaved }: EditProductModalProps) {
  const [name, setName] = useState(product.name);
  const [categoryId, setCategoryId] = useState(product.categoryId || '');
  const [categoryName, setCategoryName] = useState(product.category || '');
  const [price, setPrice] = useState(String(product.price));
  const [costPrice, setCostPrice] = useState(product.costPrice !== undefined ? String(product.costPrice) : '');
  const [sellingPrice, setSellingPrice] = useState(product.sellingPrice !== undefined ? String(product.sellingPrice) : '');
  const [stockCount, setStockCount] = useState(String(product.stockCount));
  const [unitType, setUnitType] = useState(product.unitType || 'Pieces');
  const [coreCharge, setCoreCharge] = useState(product.coreCharge !== undefined ? String(product.coreCharge) : '');
  const [barcode, setBarcode] = useState(product.barcode || '');
  const [productImage, setProductImage] = useState(product.productImage || '');
  const [returnPolicy, setReturnPolicy] = useState(product.returnPolicy || '');
  const [warrantyPeriod, setWarrantyPeriod] = useState(product.warrantyPeriod || '');
  const [baseModel, setBaseModel] = useState(product.baseModel || '');
  const [lunaVisibility, setLunaVisibility] = useState(product.lunaVisibility || 'PUBLIC');
  const [minPrice, setMinPrice] = useState(product.minPrice !== undefined && product.minPrice !== null ? String(product.minPrice) : '');
  const [maxPrice, setMaxPrice] = useState(product.maxPrice !== undefined && product.maxPrice !== null ? String(product.maxPrice) : '');
  const [expiryDate, setExpiryDate] = useState(product.expiryDate || '');
  const [categories, setCategories] = useState<Category[]>([]);
  // Multiple prices
  const [priceList, setPriceList] = useState<Array<{ label: string; price: string }>>(
    (product.priceList || []).map(p => ({ label: p.label, price: String(p.price) }))
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const [assignedOtherProduct, setAssignedOtherProduct] = useState<any>(null);

  // Real-time Barcode Collision Check for edited product
  useEffect(() => {
    const trimmed = barcode.trim();
    if (!trimmed || trimmed.length < 2 || trimmed === (product.barcode || '').trim()) {
      setAssignedOtherProduct(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('kangavena:cashierToken') || '';
        const headers: Record<string, string> = {
          'x-store-api-key': storeApiKey
        };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`/api/inventory/check-barcode?barcode=${encodeURIComponent(trimmed)}`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data?.exists && data?.product?.id !== product.id) {
            setAssignedOtherProduct(data.product);
          } else {
            setAssignedOtherProduct(null);
          }
        }
      } catch {
        // Non-blocking
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [barcode, product.barcode, product.id, storeApiKey]);

  // Fetch categories on mount
  useEffect(() => {
    fetch('/api/categories?storeId=16', { headers: { 'x-store-api-key': storeApiKey } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [storeApiKey]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body: Record<string, any> = {};
      if (name !== product.name) body.name = name;
      // Category — send name (backend findOrCreate handles new categories)
      if (categoryName !== (product.category || '')) {
        body.category = categoryName;
        const match = categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase());
        body.categoryId = match ? match.id : null;
      }
      if (price !== String(product.price)) body.price = parseFloat(price);
      if (costPrice !== (product.costPrice !== undefined ? String(product.costPrice) : '')) body.costPrice = costPrice ? parseFloat(costPrice) : null;
      if (sellingPrice !== (product.sellingPrice !== undefined ? String(product.sellingPrice) : '')) body.sellingPrice = sellingPrice ? parseFloat(sellingPrice) : null;
      if (stockCount !== String(product.stockCount)) body.stockCount = parseInt(stockCount, 10);
      if (unitType !== (product.unitType || 'Pieces')) body.unitType = unitType;
      if (coreCharge !== (product.coreCharge !== undefined ? String(product.coreCharge) : '')) body.coreCharge = coreCharge ? parseFloat(coreCharge) : null;
      if (barcode !== (product.barcode || '')) body.barcode = barcode || null;
      if (productImage !== (product.productImage || '')) body.productImage = productImage || null;
      if (returnPolicy !== (product.returnPolicy || '')) body.returnPolicy = returnPolicy || null;
      if (warrantyPeriod !== (product.warrantyPeriod || '')) body.warrantyPeriod = warrantyPeriod || null;
      if (baseModel !== (product.baseModel || '')) body.baseModel = baseModel || null;
      if (lunaVisibility !== (product.lunaVisibility || 'PUBLIC')) body.lunaVisibility = lunaVisibility;
      const origMinPrice = product.minPrice !== undefined && product.minPrice !== null ? String(product.minPrice) : '';
      if (minPrice !== origMinPrice) body.minPrice = minPrice ? parseFloat(minPrice) : null;
      const origMaxPrice = product.maxPrice !== undefined && product.maxPrice !== null ? String(product.maxPrice) : '';
      if (maxPrice !== origMaxPrice) body.maxPrice = maxPrice ? parseFloat(maxPrice) : null;
      if (expiryDate !== (product.expiryDate || '')) body.expiryDate = expiryDate || null;
      // Multiple prices
      const cleanPriceList = priceList
        .filter(p => p.label.trim() && p.price && parseFloat(p.price) > 0)
        .map(p => ({ label: p.label.trim(), price: parseFloat(p.price) }));
      const origPriceList = (product.priceList || []).map(p => ({ label: p.label, price: p.price }));
      if (JSON.stringify(cleanPriceList) !== JSON.stringify(origPriceList)) {
        body.priceList = cleanPriceList.length > 0 ? cleanPriceList : null;
      }

      if (Object.keys(body).length === 0) { onClose(); return; }

      await axios.put(`/api/products/${product.id}`, body, {
        headers: { 'x-store-api-key': storeApiKey, 'Content-Type': 'application/json' },
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`/api/products/${product.id}`, {
        headers: { 'x-store-api-key': storeApiKey },
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete');
      setDeleting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-800">Edit Product</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
              <AlertTriangle size={14} />
              {error}
            </div>
          )}

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Category</label>
              <input type="text" value={categoryName}
                onChange={e => {
                  const val = e.target.value;
                  setCategoryName(val);
                  const match = categories.find(c => c.name.toLowerCase() === val.toLowerCase());
                  setCategoryId(match ? match.id : '');
                }}
                list="edit-categories"
                placeholder="Type or select a category"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
              <datalist id="edit-categories">
                {categories.map(c => <option key={c.id} value={c.name} />)}
              </datalist>

              {/* 🤖 Auto Categorize button */}
              {name.trim() && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!name.trim()) return;
                    try {
                      const res = await fetch('/api/auto-categorize', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
                        body: JSON.stringify({
                          productNames: [name.trim()],
                          existingCategories: categories.map(c => c.name),
                        }),
                      });
                      const data = await res.json();
                      if (data.assignments && data.assignments[0]) {
                        const cat = data.assignments[0].category;
                        const match = categories.find(c => c.name.toLowerCase() === cat.toLowerCase());
                        setCategoryName(match ? match.name : cat);
                        setCategoryId(match ? String(match.id) : '');
                      }
                    } catch { /* ignore */ }
                  }}
                  className="mt-1 flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-600 text-[9px] font-bold hover:bg-violet-500/20 transition-all"
                >
                  ✨ Auto Categorize
                </button>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Unit Type</label>
              <select value={unitType} onChange={e => setUnitType(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary">
                {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Price</label>
              <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Cost Price</label>
              <input type="number" step="0.01" value={costPrice} onChange={e => setCostPrice(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Sell Price</label>
              <input type="number" step="0.01" value={sellingPrice} onChange={e => setSellingPrice(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            </div>
          </div>

          {/* ↔️ Between Price Range */}
          <div className="p-2.5 rounded-xl bg-indigo-50 border border-indigo-100 space-y-1 mt-2">
            <div className="flex items-center justify-between">
              <label className="block text-[10px] font-extrabold uppercase tracking-wider text-indigo-700">
                ↔️ Between Price Range (අතරමැදි මිල ගණන්)
              </label>
              <span className="text-[8px] font-semibold text-indigo-400">Optional</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[9px] font-semibold text-slate-500 mb-0.5">Min Price (Rs.)</label>
                <input type="number" step="0.01" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="e.g. 2000"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-mono font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              </div>
              <div>
                <label className="block text-[9px] font-semibold text-slate-500 mb-0.5">Max Price (Rs.)</label>
                <input type="number" step="0.01" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="e.g. 3000"
                  className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-mono font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
              </div>
            </div>
          </div>

          {/* ── Multiple Prices ── */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">🏷️ Additional Prices</label>
              <button
                type="button"
                onClick={() => setPriceList(prev => [...prev, { label: '', price: '' }])}
                className="flex items-center gap-1 text-[10px] font-bold text-brand-primary hover:text-brand-primary/80 transition-all"
              >
                <Plus size={12} /> Add Price
              </button>
            </div>
            <p className="text-[9px] text-slate-400 mb-2">Add alternate prices (wholesale, bulk, member, etc.). When adding to bill, you can choose which price to use.</p>

            {priceList.length > 0 && (
              <div className="space-y-2">
                {priceList.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Label (e.g. Wholesale)"
                      value={p.label}
                      onChange={e => {
                        const newList = [...priceList];
                        newList[i] = { ...newList[i], label: e.target.value };
                        setPriceList(newList);
                      }}
                      className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={p.price}
                      onChange={e => {
                        const newList = [...priceList];
                        newList[i] = { ...newList[i], price: e.target.value };
                        setPriceList(newList);
                      }}
                      className="w-28 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                    />
                    <button
                      type="button"
                      onClick={() => setPriceList(prev => prev.filter((_, j) => j !== i))}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Minus size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Stock Count</label>
              <input type="number" value={stockCount} onChange={e => setStockCount(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">📅 Expiry Date</label>
              <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Core Charge</label>
              <input type="number" step="0.01" value={coreCharge} onChange={e => setCoreCharge(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Barcode / QR Code</label>
            <div className="flex gap-2">
              <input type="text" value={barcode} onChange={e => setBarcode(e.target.value)}
                placeholder="Scan or enter barcode…"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            </div>
            {assignedOtherProduct && (
              <div className="mt-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="font-bold">Barcode already assigned to another item:</p>
                  <p className="text-[11px] text-amber-700 truncate">
                    <strong>{assignedOtherProduct.name}</strong> • Rs. {assignedOtherProduct.price || assignedOtherProduct.sellingPrice} • Stock: {assignedOtherProduct.stockCount} {assignedOtherProduct.unitType || 'Pcs'}
                  </p>
                </div>
              </div>
            )}
            {!assignedOtherProduct && barcode.trim().length >= 2 && barcode.trim() !== (product.barcode || '').trim() && (
              <p className="mt-1.5 text-[11px] text-emerald-600 font-medium flex items-center gap-1">
                <span>✨</span> Barcode is available and unique for this store.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Return Policy</label>
              <input type="text" maxLength={100} value={returnPolicy} onChange={e => setReturnPolicy(e.target.value)}
                list="edit-return-policies"
                placeholder="e.g. 14 Days, No Return, Standard"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
              <datalist id="edit-return-policies">
                <option value="14 Days" /><option value="7 Days" /><option value="3 Days" /><option value="No Return" /><option value="Standard" />
              </datalist>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Warranty Period</label>
              <input type="text" maxLength={100} value={warrantyPeriod} onChange={e => setWarrantyPeriod(e.target.value)}
                list="edit-warranty-periods"
                placeholder="e.g. 3 Months"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
              <datalist id="edit-warranty-periods">
                <option value="6 Months" /><option value="1 Year" /><option value="2 Years" /><option value="Lifetime" /><option value="No Warranty" />
              </datalist>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Base Model (Parent Model)</label>
            <input type="text" maxLength={100} value={baseModel} onChange={e => setBaseModel(e.target.value)}
              list="edit-base-models"
              placeholder="e.g. Toyota Prius, iPhone 15, Honda Dio"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" />
            <datalist id="edit-base-models">
              <option value="Toyota Prius" /><option value="Honda Civic" /><option value="Suzuki Alto" /><option value="Nissan Leaf" />
              <option value="iPhone 15" /><option value="Samsung Galaxy" /><option value="Honda Dio" /><option value="Yamaha FZ" />
            </datalist>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Luna AI Visibility</label>
            <select value={lunaVisibility} onChange={e => setLunaVisibility(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary">
              <option value="PUBLIC">🌐 Public — visible with price</option>
              <option value="HIDDEN">🚫 Hidden — excluded from Luna</option>
              <option value="PRICE_HIDDEN">🔒 Hide Price — visible, no price shown</option>
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Product Image</label>
            <div className="flex items-center gap-3 mb-2">
              {productImage ? (
                <div className="relative group">
                  <img src={productImage} alt="preview" className="w-16 h-16 object-cover rounded-xl border border-slate-200 shadow-sm" />
                  <button
                    type="button"
                    onClick={() => setProductImage('')}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 text-slate-400">
                  <ImageIcon size={20} />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const compressed = await compressProductImage(file, 512, 512, 0.85);
                        setProductImage(compressed);
                      } catch {
                        const reader = new FileReader();
                        reader.onload = (ev) => setProductImage(ev.target?.result as string);
                        reader.readAsDataURL(file);
                      }
                    }
                  }}
                  className="block w-full text-[10px] text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-brand-primary/10 file:text-brand-primary file:text-[10px] file:font-semibold hover:file:bg-brand-primary/20 file:cursor-pointer"
                />
                <p className="text-[8px] text-slate-400 font-mono mt-1">✨ Auto-compressed to 512×512px</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-slate-200 gap-3">
          <button onClick={handleDelete}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
              confirmDelete
                ? 'bg-red-600 text-white shadow-md shadow-red-600/30'
                : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
            }`}
            disabled={deleting}
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {confirmDelete ? 'Confirm Delete' : 'Delete'}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-brand-primary text-brand-text-on-primary text-[10px] font-black uppercase tracking-widest shadow-md shadow-brand-primary/20 hover:brightness-110 transition-all"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
